/**
 * Medical Chart
 * Chart to display Lab Result and Vital Sign data
 */
import { useTheme } from '@mui/material/styles';
import { startOfDay } from 'date-fns';
import { addHours } from 'date-fns/esm';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { HealthLineChart } from '@components/Charts/Line';
import RoundedArrowButton from '@components/RoundedArrowButton';
import RoundedDropdown from '@components/RoundedDropdown';

import { useHealthChart } from '@hooks/useHealthChart';

import type { DataPointMapped } from '@models/data-point';
import { Interval } from '@models/graph';
import { INTERVALS } from '@models/graph';

import { offsetDate } from '@utils/date';

import DatePicker from './DatePicker/index';
import { ChartOptionsWrapper, ChartStyle, TopBar } from './MedicalChart.style';

const messages = defineMessages({
  day: {
    id: 'medicalChart.day',
    defaultMessage: 'Day',
  },
  week: {
    id: 'medicalChart.week',
    defaultMessage: 'Week',
  },
  month: {
    id: 'medicalChart.month',
    defaultMessage: 'Month',
  },
  year: {
    id: 'medicalChart.year',
    defaultMessage: 'Year',
  },
});

interface MedicalChartProps {
  data: DataPointMapped[];
  width?: string;
  interval?: Interval;
  code: string;
}

export default function MedicalChart({
  data,
  width = '600px',
  code,
}: MedicalChartProps) {
  const { interval, maxDate, minDate, setDateFilters } = useHealthChart();
  const [dateFormat, setDateFormat] = useState('MMM D, YYYY');
  const { formatMessage } = useIntl();
  const { palette } = useTheme();
  const range = INTERVALS[interval];

  const handleRefetch = async (intervalNum: number, min?: Date, max?: Date) => {
    if (!min || !max) {
      return;
    }
    const dateFrom = dayjs(min).startOf('day').toISOString();
    const dateTo = dayjs(max).endOf('day').toISOString();

    setDateFilters({
      maxDate: new Date(dateTo),
      minDate: new Date(dateFrom),
      interval:
        intervalNum === INTERVALS.Day
          ? Interval.Day
          : intervalNum === INTERVALS.Week
            ? Interval.Week
            : intervalNum === INTERVALS.Month
              ? Interval.Month
              : intervalNum === INTERVALS.Year
                ? Interval.Year
                : intervalNum === INTERVALS.Hour
                  ? Interval.Hour
                  : undefined,
    });
  };

  async function navigateToDate(date: Date, range: number) {
    let min: Date | undefined;
    let max: Date | undefined;
    if (range !== INTERVALS.Day) {
      max = date;
    }
    switch (range) {
      case INTERVALS.Year:
        min = offsetDate(0, 0, -1, date);
        break;
      case INTERVALS.Month:
        min = offsetDate(0, -1, 0, date);
        break;
      case INTERVALS.Week:
        min = offsetDate(-7, 0, 0, date);
        break;
      case INTERVALS.Day:
      default:
        min = offsetDate(0, 0, 0, startOfDay(date));
        max = addHours(date, 24);
        break;
    }
    await handleRefetch(range, min, max);
  }

  async function changeRange(range: number, date: Date) {
    switch (range) {
      case INTERVALS.Year:
        setDateFormat('YYYY');
        break;
      case INTERVALS.Month:
        setDateFormat('MMM YYYY');
        break;
      case INTERVALS.Week:
        setDateFormat('MMM D, YYYY');
        break;
      case INTERVALS.Day:
      default:
        setDateFormat('MMM D, YYYY');
        break;
    }

    await navigateToDate(date, range);
  }

  async function changePage(direction: number, range: number) {
    let min: Date | undefined;
    let max: Date | undefined;
    switch (range) {
      case INTERVALS.Year:
        max = offsetDate(0, Number(direction), 0, maxDate);
        min = offsetDate(0, Number(direction), 0, minDate);
        // setCurrentDate(offsetDate(0, Number(direction), 0, minDate));
        break;
      case INTERVALS.Month:
        max = offsetDate(7 * direction, 0, 0, maxDate);
        min = offsetDate(7 * direction, 0, 0, minDate);
        //setCurrentDate(offsetDate(7 * direction, 0, 0, minDate));
        break;
      case INTERVALS.Week:
        max = offsetDate(direction, 0, 0, maxDate);
        min = offsetDate(direction, 0, 0, minDate);
        //setCurrentDate(offsetDate(direction, 0, 0, minDate));
        break;
      case INTERVALS.Day:
      default:
        max = addHours(maxDate, direction);
        min = addHours(minDate, direction);
        // setCurrentDate(addHours(minDate, direction));
        break;
    }

    await handleRefetch(range, min, max);
  }

  return (
    <ChartStyle width={width}>
      <TopBar>
        <RoundedArrowButton
          onClick={() => changePage(-1, range)}
          rotation={90}
        />
        <ChartOptionsWrapper>
          <RoundedDropdown
            onChange={(range) => changeRange(range as number, maxDate)}
            options={[
              { text: formatMessage(messages.day), value: INTERVALS.Day },
              { text: formatMessage(messages.week), value: INTERVALS.Week },
              { text: formatMessage(messages.month), value: INTERVALS.Month },
              { text: formatMessage(messages.year), value: INTERVALS.Year },
            ]}
            value={range}
          />
          <DatePicker
            label={
              range === INTERVALS.Day
                ? dayjs(minDate).format(dateFormat)
                : `${dayjs(minDate).format(dateFormat)} - ${dayjs(
                    maxDate
                  ).format(dateFormat)}`
            }
            onChange={(date) => {
              if (date) {
                navigateToDate(date, range);
                // setCurrentDate(date);
              }
            }}
            value={maxDate}
          />
        </ChartOptionsWrapper>
        <RoundedArrowButton
          onClick={() => changePage(+1, range)}
          rotation={270}
        />
      </TopBar>

      <HealthLineChart
        code={code}
        data={data}
        options={{
          colors: {
            font: palette.primary.contrastText,
            grid: `${palette.divider}24`,
          },
        }}
      />
    </ChartStyle>
  );
}
